import axiosInstance from '@/api/axiosInstance';
import {
  CERTIFICATE_ENDPOINTS, COURSE_CONTENT_ENDPOINTS, QUESTIONNAIRE_ENDPOINTS,
} from '@/api/endpoints/course-content.endpoints';
import { API_PREFIX, getAbsoluteApiUrl } from '@/api/endpoints/api.constants';
import { tokenStorage } from '@/api/tokenManager';
import {
  ApiTopicCreate, ApiTopic, ApiTopicUpdate,
  ApiActivityCreate, ApiActivity, ApiActivityUpdate, ApiRecycleBinActivity,
  ApiBlockCreate, ApiBlock, ApiBlockUpdate,
  BlockCompletionRequest, BlockCompletionResponse, UserActivityProgress,
  ApiMediaLibraryResponse, ApiMediaLibraryParams,
  ApiQuestionnaireCreate, ApiQuestionnaire, ApiQuestionnaireUpdate,
  ApiQuestionnaireSubmissionRequest, ApiQuestionnaireSubmissionResponse,
  ApiCertificateTemplate, ApiCertificateTemplateCreate, ApiCertificateTemplateUpdate,
  ApiCertificateIssueRequest, ApiCertificateIssueResponse, ApiCertificateVerificationResponse,
  ApiCertificateActivity, ApiCertificateActivityUpdate, ApiCertificateIssuedRow, ApiCertificateFont, ApiCertificateLearner, UserCertificate,
  ApiScormPackageInfo, ApiScormTrackRequest,
  ApiGetXapiStatementsParams, ApiXapiStatement, XapiStatementPayload,
} from '@/components/admin/Courses/CourseBuilder/types/courseContentApi.types';
import { ManagementApiService } from '@/types/management.types';

import { tr } from '@/i18n';
export const TopicApiService = {
  createTopic: async (data: ApiTopicCreate): Promise<ApiTopic> =>
    (await axiosInstance.post<ApiTopic>(COURSE_CONTENT_ENDPOINTS.TOPICS, data)).data,
  updateTopic: async (topicId: number, data: ApiTopicUpdate): Promise<ApiTopic> =>
    (await axiosInstance.put<ApiTopic>(COURSE_CONTENT_ENDPOINTS.TOPIC_DETAIL(topicId), data)).data,
  deleteTopic: async (topicId: number): Promise<void> => {
    await axiosInstance.delete(COURSE_CONTENT_ENDPOINTS.TOPIC_DETAIL(topicId));
  },
  /**
   * @param raw when true, keep `{en}…{ar}…` multilang tags in the title so the
   *   module settings drawer's per-language editor round-trips every
   *   translation. Display callers omit it (interceptor strips to locale).
   */
  getTopicById: async (topicId: number, raw = false): Promise<ApiTopic> =>
    (await axiosInstance.get<ApiTopic>(
      COURSE_CONTENT_ENDPOINTS.TOPIC_DETAIL(topicId),
      raw ? ({ skipMultilangStrip: true } as object) : undefined,
    )).data,
  listTopicsForCourse: async (courseId: number): Promise<ApiTopic[]> =>
    (await axiosInstance.get<ApiTopic[]>(COURSE_CONTENT_ENDPOINTS.TOPICS_BY_COURSE(courseId))).data,
};

export const ActivityApiService = {
  createActivity: async (data: ApiActivityCreate): Promise<ApiActivity> =>
    (await axiosInstance.post<ApiActivity>(COURSE_CONTENT_ENDPOINTS.ACTIVITIES, data)).data,
  createStudioActivity: async (data: { topic_id: number; title: string; order?: number }): Promise<ApiActivity> =>
    (await axiosInstance.post<ApiActivity>(COURSE_CONTENT_ENDPOINTS.STUDIO_LESSON_ACTIVITY, data)).data,
  ensureStudioLink: async (activityId: number): Promise<ApiActivity> =>
    (await axiosInstance.post<ApiActivity>(COURSE_CONTENT_ENDPOINTS.STUDIO_LINK_ACTIVITY(activityId), {})).data,
  updateActivity: async (activityId: number, data: ApiActivityUpdate): Promise<ApiActivity> =>
    (await axiosInstance.put<ApiActivity>(COURSE_CONTENT_ENDPOINTS.ACTIVITY_DETAIL(activityId), data)).data,
  /** Soft delete: moves the activity to the course Recycle Bin. */
  deleteActivity: async (activityId: number): Promise<void> => {
    await axiosInstance.delete(COURSE_CONTENT_ENDPOINTS.ACTIVITY_DETAIL(activityId));
  },
  /**
   * @param raw when true, keep `{en}…{ar}…` multilang tags in title/description
   *   so the activity edit drawer's per-language editors round-trip every
   *   translation. Display callers omit it (interceptor strips to locale).
   */
  getActivityById: async (activityId: number, raw = false): Promise<ApiActivity> =>
    (await axiosInstance.get<ApiActivity>(
      COURSE_CONTENT_ENDPOINTS.ACTIVITY_DETAIL(activityId),
      raw ? ({ skipMultilangStrip: true } as object) : undefined,
    )).data,
  listActivitiesForTopic: async (topicId: number): Promise<ApiActivity[]> =>
    (await axiosInstance.get<ApiActivity[]>(COURSE_CONTENT_ENDPOINTS.ACTIVITIES_BY_TOPIC(topicId))).data,
  /** Duplicate an activity (settings + blocks) into the same module. */
  duplicateActivity: async (activityId: number): Promise<ApiActivity> =>
    (await axiosInstance.post<ApiActivity>(COURSE_CONTENT_ENDPOINTS.DUPLICATE_ACTIVITY(activityId))).data,
  /** Activities currently sitting in the course Recycle Bin. */
  listRecycleBin: async (courseId: number): Promise<ApiRecycleBinActivity[]> =>
    (await axiosInstance.get<ApiRecycleBinActivity[]>(COURSE_CONTENT_ENDPOINTS.RECYCLE_BIN(courseId))).data,
  /** Restore a soft-deleted activity out of the Recycle Bin. */
  restoreActivity: async (activityId: number): Promise<ApiActivity> =>
    (await axiosInstance.post<ApiActivity>(COURSE_CONTENT_ENDPOINTS.RESTORE_ACTIVITY(activityId))).data,
  /** Permanently delete an activity from the Recycle Bin (cannot be undone). */
  purgeActivity: async (activityId: number): Promise<void> => {
    await axiosInstance.delete(COURSE_CONTENT_ENDPOINTS.PURGE_ACTIVITY(activityId));
  },
};

export const BlockApiService = {
  getBlockById: async (blockId: number): Promise<ApiBlock> =>
    (await axiosInstance.get<ApiBlock>(COURSE_CONTENT_ENDPOINTS.BLOCK_DETAIL(blockId))).data,
  addBlockToActivity: async (activityId: number, data: ApiBlockCreate): Promise<ApiBlock> =>
    (await axiosInstance.post<ApiBlock>(COURSE_CONTENT_ENDPOINTS.BLOCKS_BY_ACTIVITY(activityId), data)).data,
  updateActivityBlock: async (blockId: number, data: ApiBlockUpdate): Promise<ApiBlock> =>
    (await axiosInstance.put<ApiBlock>(COURSE_CONTENT_ENDPOINTS.BLOCK_DETAIL(blockId), data)).data,
  deleteActivityBlock: async (blockId: number): Promise<void> => {
    await axiosInstance.delete(COURSE_CONTENT_ENDPOINTS.BLOCK_DETAIL(blockId));
  },
  listActivityBlocks: async (activityId: number): Promise<ApiBlock[]> =>
    (await axiosInstance.get<ApiBlock[]>(COURSE_CONTENT_ENDPOINTS.BLOCKS_BY_ACTIVITY(activityId))).data,
  reorderActivityBlocks: async (activityId: number, blockIds: number[]): Promise<{ message: string }> =>
    (await axiosInstance.post<{ message: string }>(COURSE_CONTENT_ENDPOINTS.REORDER_BLOCKS(activityId), blockIds)).data,
};

export const ProgressApiService = {
  markBlockComplete: async (blockId: number, data: BlockCompletionRequest): Promise<BlockCompletionResponse> =>
    (await axiosInstance.post<BlockCompletionResponse>(COURSE_CONTENT_ENDPOINTS.MARK_BLOCK_COMPLETE(blockId), data)).data,
  getUserActivityProgress: async (activityId: number): Promise<UserActivityProgress> =>
    (await axiosInstance.get<UserActivityProgress>(COURSE_CONTENT_ENDPOINTS.USER_ACTIVITY_PROGRESS(activityId))).data,
  listProgressForActivities: async (activityIds: number[]): Promise<Map<number, UserActivityProgress>> => {
    const progressMap = new Map<number, UserActivityProgress>();
    await Promise.all(
      activityIds.map((id) =>
        axiosInstance.get<UserActivityProgress>(COURSE_CONTENT_ENDPOINTS.USER_ACTIVITY_PROGRESS(id))
          .then((res) => progressMap.set(id, res.data))
          .catch(() => progressMap.set(id, []))
      )
    );
    return progressMap;
  },
  /** Activity-level Mark Complete (Studio activities + any other no-blocks
   *  activity types). Returns the persisted CourseActivityProgress row. */
  markActivityComplete: async (activityId: number) =>
    (
      await axiosInstance.post(
        `/v1/course-content/activities/${activityId}/mark-complete`,
      )
    ).data,
  /** How far the learner got in a video activity (seconds and percent, kept on the server). */
  getVideoProgress: async (activityId: number): Promise<VideoProgress> =>
    (await axiosInstance.get<VideoProgress>(`/v1/course-content/activities/${activityId}/video-progress`)).data,
  /** Report the current position; the server keeps the furthest point and completes the activity at its threshold.
   *  `leaving` sends it with keepalive so it still arrives while the page unloads. */
  putVideoProgress: async (activityId: number, position: number, duration: number, leaving = false): Promise<VideoProgress | null> => {
    const body = { position: Math.max(0, position || 0), duration: Math.max(0, duration || 0) };
    if (!leaving) {
      return (await axiosInstance.put<VideoProgress>(`/v1/course-content/activities/${activityId}/video-progress`, body)).data;
    }
    const token = tokenStorage.getAccessToken();
    await fetch(`${API_PREFIX}/course-content/activities/${activityId}/video-progress`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': localStorage.getItem('tenant_id') || '1',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    }).catch(() => undefined);
    return null;
  },
};

export interface VideoProgress {
  furthest: number;
  last: number;
  duration: number;
  percent: number;
  status: string;
}

export const ContentLibraryApiService = {
  getCourseMedia: async (params: ApiMediaLibraryParams): Promise<ApiMediaLibraryResponse> =>
    (await axiosInstance.get<ApiMediaLibraryResponse>(COURSE_CONTENT_ENDPOINTS.CONTENT_LIBRARY, { params })).data,
};

export const QuestionnaireApiService = {
  createQuestionnaire: async (data: ApiQuestionnaireCreate): Promise<ApiQuestionnaire> =>
    (await axiosInstance.post<ApiQuestionnaire>(QUESTIONNAIRE_ENDPOINTS.QUESTIONNAIRES, data)).data,
  listQuestionnairesForCourse: async (courseId: number): Promise<ApiQuestionnaire[]> =>
    (await axiosInstance.get<ApiQuestionnaire[]>(QUESTIONNAIRE_ENDPOINTS.QUESTIONNAIRES_BY_COURSE(courseId))).data,
  getQuestionnaire: async (questionnaireId: number): Promise<ApiQuestionnaire> =>
    (await axiosInstance.get<ApiQuestionnaire>(QUESTIONNAIRE_ENDPOINTS.QUESTIONNAIRE_DETAIL(questionnaireId))).data,
  updateQuestionnaire: async (questionnaireId: number, data: ApiQuestionnaireUpdate): Promise<ApiQuestionnaire> =>
    (await axiosInstance.put<ApiQuestionnaire>(QUESTIONNAIRE_ENDPOINTS.QUESTIONNAIRE_DETAIL(questionnaireId), data)).data,
  deleteQuestionnaire: async (questionnaireId: number): Promise<void> => {
    await axiosInstance.delete(QUESTIONNAIRE_ENDPOINTS.QUESTIONNAIRE_DETAIL(questionnaireId));
  },
  submitQuestionnaire: async (questionnaireId: number, data: ApiQuestionnaireSubmissionRequest): Promise<ApiQuestionnaireSubmissionResponse> =>
    (await axiosInstance.post<ApiQuestionnaireSubmissionResponse>(QUESTIONNAIRE_ENDPOINTS.SUBMIT_QUESTIONNAIRE(questionnaireId), data)).data,
};

export const CertificateApiService = {
  /** With a course: only what that course's certificate can use (site course templates + its own designs). */
  /** All templates; for a course, what its activities can use; ``forPrograms``: program certificates only. */
  listCertificateTemplates: async (courseId?: number, opts?: { forPrograms?: boolean }): Promise<ApiCertificateTemplate[]> =>
    (await axiosInstance.get<ApiCertificateTemplate[]>(CERTIFICATE_ENDPOINTS.TEMPLATES, {
      params: opts?.forPrograms ? { for_programs: true } : courseId ? { course_id: courseId } : undefined,
    })).data,
  createCertificateTemplate: async (data: ApiCertificateTemplateCreate): Promise<ApiCertificateTemplate> =>
    (await axiosInstance.post<ApiCertificateTemplate>(CERTIFICATE_ENDPOINTS.TEMPLATES, data)).data,
  getCertificateTemplate: async (templateId: number): Promise<ApiCertificateTemplate> =>
    (await axiosInstance.get<ApiCertificateTemplate>(CERTIFICATE_ENDPOINTS.TEMPLATE_DETAIL(templateId))).data,
  updateCertificateTemplate: async (templateId: number, data: ApiCertificateTemplateUpdate): Promise<ApiCertificateTemplate> =>
    (await axiosInstance.put<ApiCertificateTemplate>(CERTIFICATE_ENDPOINTS.TEMPLATE_DETAIL(templateId), data)).data,
  deleteCertificateTemplate: async (templateId: number): Promise<void> => {
    await axiosInstance.delete(CERTIFICATE_ENDPOINTS.TEMPLATE_DETAIL(templateId));
  },
  issueCertificate: async (data: ApiCertificateIssueRequest): Promise<ApiCertificateIssueResponse> =>
    (await axiosInstance.post<ApiCertificateIssueResponse>(CERTIFICATE_ENDPOINTS.ISSUE, data)).data,
  downloadCertificate: async (issueId: number): Promise<Blob> =>
    (await axiosInstance.get<Blob>(CERTIFICATE_ENDPOINTS.DOWNLOAD(issueId), { responseType: 'blob' })).data,
  verifyCertificate: async (certId: string): Promise<ApiCertificateVerificationResponse> =>
    (await axiosInstance.get<ApiCertificateVerificationResponse>(CERTIFICATE_ENDPOINTS.VERIFY(certId))).data,

  // ── Certificate ACTIVITY config (Design Certificate + issue trigger) ──
  getActivityConfig: async (activityId: number): Promise<ApiCertificateActivity> =>
    (await axiosInstance.get<ApiCertificateActivity>(CERTIFICATE_ENDPOINTS.ACTIVITY_CONFIG(activityId))).data,
  updateActivityConfig: async (activityId: number, data: ApiCertificateActivityUpdate): Promise<ApiCertificateActivity> =>
    (await axiosInstance.put<ApiCertificateActivity>(CERTIFICATE_ENDPOINTS.ACTIVITY_CONFIG(activityId), data)).data,
  issueForActivity: async (activityId: number, userId: number): Promise<ApiCertificateIssueResponse> =>
    (await axiosInstance.post<ApiCertificateIssueResponse>(`${CERTIFICATE_ENDPOINTS.ACTIVITY_ISSUE(activityId)}?user_id=${userId}`)).data,
  /** The course's enrolled learners and whether each holds this certificate. */
  activityLearners: async (activityId: number): Promise<ApiCertificateLearner[]> =>
    (await axiosInstance.get<ApiCertificateLearner[]>(CERTIFICATE_ENDPOINTS.ACTIVITY_LEARNERS(activityId))).data,
  issueMany: async (activityId: number, userIds: number[]): Promise<{ issued: number; skipped: number; failed: number[] }> =>
    (await axiosInstance.post(CERTIFICATE_ENDPOINTS.ACTIVITY_ISSUE_MANY(activityId), { user_ids: userIds })).data,
  claimMyCertificate: async (activityId: number): Promise<ApiCertificateIssueResponse | null> =>
    (await axiosInstance.post<ApiCertificateIssueResponse | null>(CERTIFICATE_ENDPOINTS.ACTIVITY_CLAIM(activityId))).data,

  // ── Issued report + actions ──
  listIssuedForCourse: async (
    courseId: number,
    params?: { activity_id?: number; user_id?: number; status_filter?: string; date_from?: string; date_to?: string },
  ): Promise<ApiCertificateIssuedRow[]> =>
    (await axiosInstance.get<ApiCertificateIssuedRow[]>(CERTIFICATE_ENDPOINTS.COURSE_ISSUED(courseId), { params })).data,
  regenerateCertificate: async (issueId: number): Promise<ApiCertificateIssueResponse> =>
    (await axiosInstance.post<ApiCertificateIssueResponse>(CERTIFICATE_ENDPOINTS.REGENERATE(issueId))).data,
  /** Sample PDF of a (possibly unsaved) design, from the issued-certificate renderer. */
  previewDesign: async (design: { layout_json: unknown; page_size?: string | null; orientation?: string | null; background_image_id?: number | null }): Promise<Blob> =>
    (await axiosInstance.post<Blob>(CERTIFICATE_ENDPOINTS.TEMPLATE_PREVIEW, design, { responseType: 'blob' })).data,
  listFonts: async (): Promise<ApiCertificateFont[]> =>
    (await axiosInstance.get<ApiCertificateFont[]>(CERTIFICATE_ENDPOINTS.FONTS)).data,
  uploadFont: async (file: File): Promise<ApiCertificateFont> => {
    const form = new FormData();
    form.append('file', file);
    return (await axiosInstance.post<ApiCertificateFont>(CERTIFICATE_ENDPOINTS.FONTS, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  deleteFont: async (fontId: number): Promise<void> => {
    await axiosInstance.delete(CERTIFICATE_ENDPOINTS.FONT(fontId));
  },
  /** The font file itself, so the designer can preview it (FontFace). */
  fontData: async (fontId: number): Promise<ArrayBuffer> =>
    (await axiosInstance.get<ArrayBuffer>(CERTIFICATE_ENDPOINTS.FONT_FILE(fontId), { responseType: 'arraybuffer' })).data,
  builtinFontData: async (key: string, weight: 'regular' | 'bold'): Promise<ArrayBuffer> =>
    (await axiosInstance.get<ArrayBuffer>(CERTIFICATE_ENDPOINTS.BUILTIN_FONT(key), { params: { weight }, responseType: 'arraybuffer' })).data,
  revokeCertificate: async (issueId: number): Promise<ApiCertificateIssueResponse> =>
    (await axiosInstance.post<ApiCertificateIssueResponse>(CERTIFICATE_ENDPOINTS.REVOKE(issueId))).data,
  downloadCourseCertificatesZip: async (
    courseId: number,
    params?: { activity_id?: number; user_id?: number; status_filter?: string; date_from?: string; date_to?: string },
  ): Promise<Blob> =>
    (await axiosInstance.get<Blob>(CERTIFICATE_ENDPOINTS.COURSE_ISSUED_ZIP(courseId), { params, responseType: 'blob' })).data,
  listByUser: async (userId: number): Promise<UserCertificate[]> =>
    (await axiosInstance.get<UserCertificate[]>(CERTIFICATE_ENDPOINTS.BY_USER(userId))).data,
};

export const CertificateTemplateManagementApiService: ManagementApiService<ApiCertificateTemplate, {}> = {
  getAll: async (offset = 0, limit = 500) => {
    const items = await CertificateApiService.listCertificateTemplates();
    return { total: items.length, limit, offset, items: items.slice(offset, offset + limit) };
  },
  deleteItem: CertificateApiService.deleteCertificateTemplate,
};

export const LearningObjectApiService = {
  uploadScormPackage: async (
    activityBlockId: number,
    file: File,
    onProgress?: (percent: number) => void,
    options?: { confirmReplace?: boolean },
  ): Promise<ApiScormPackageInfo> => {
    // We use XMLHttpRequest directly here for two reasons:
    //   1. The shared axios instance forces Content-Type: application/json
    //      via defaults, which strips the multipart boundary even when we
    //      try to nullify it on a per-request basis. Stripping the
    //      boundary makes Starlette see zero parts in the body.
    //   2. We need real upload progress (fetch() has none).
    // Vite's dev proxy also drops large multipart bodies, so we target the
    // backend origin directly — CORS already whitelists this origin.
    const url = getAbsoluteApiUrl(
      COURSE_CONTENT_ENDPOINTS.UPLOAD_SCORM(activityBlockId).replace(/^\/v1/, ''),
    );
    const formData = new FormData();
    // Pass the filename explicitly. Without it, some browsers omit the
    // `filename=...` parameter from the part's Content-Disposition, and
    // Starlette then parses the part as a plain string instead of an
    // UploadFile — which fails our isinstance check on the backend.
    formData.append('file', file, file.name || 'package.zip');
    if (options?.confirmReplace) {
      formData.append('confirm_replace', 'true');
    }

    return new Promise<ApiScormPackageInfo>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);

      const token = tokenStorage.getAccessToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      const tenantId = localStorage.getItem('tenant_id');
      if (tenantId) {
        xhr.setRequestHeader('X-Tenant-ID', tenantId);
        xhr.setRequestHeader('Tenant-Id', tenantId);
      }
      // Intentionally NOT setting Content-Type — the browser fills it in
      // with `multipart/form-data; boundary=...`.

      if (onProgress) {
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable && evt.total > 0) {
            onProgress(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as ApiScormPackageInfo);
          } catch (e) {
            reject(e);
          }
        } else {
          // Build a thin axios-shaped error so the caller's existing
          // `err.response.data.detail` handling still works.
          let parsed: any = null;
          try { parsed = JSON.parse(xhr.responseText); } catch { /* ignore */ }
          const err: any = new Error(
            parsed?.detail || tr("ui.upload_failed_with_status_x", "Upload failed with status {v0}", { v0: xhr.status }),
          );
          err.response = { status: xhr.status, data: parsed };
          reject(err);
        }
      };
      xhr.onerror = () => reject(new Error(tr("ui.network_error_during_scorm_upload", "Network error during SCORM upload.")));
      xhr.onabort = () => reject(new Error(tr("ui.scorm_upload_aborted", "SCORM upload aborted.")));
      xhr.send(formData);
    });
  },
  launchScormPlayer: async (activityBlockId: number): Promise<string> =>
    (await axiosInstance.get<string>(COURSE_CONTENT_ENDPOINTS.LAUNCH_SCORM_PLAYER(activityBlockId), { responseType: 'text' })).data,
  /** Mint an iframe-friendly player URL.
   *
   *  The backend stashes the short-lived session token in an HttpOnly cookie
   *  instead of the URL query — keeping the JWT out of browser history,
   *  console source-map lines, and access logs. We must call the launch-url
   *  endpoint cross-origin (port 8000) with credentials so the Set-Cookie is
   *  honoured; the same cookie is then auto-sent when the iframe loads the
   *  returned player URL.
   */
  /** `mode`: resume (default — or review when the last attempt is finished), `new` attempt, or `review`. */
  getScormLaunchUrl: async (
    activityBlockId: number,
    opts: { mode?: 'resume' | 'new' | 'review'; attempt?: number } = {},
  ): Promise<ScormLaunch> => {
    const url = getAbsoluteApiUrl(
      COURSE_CONTENT_ENDPOINTS.GET_SCORM_LAUNCH_URL(activityBlockId).replace(/^\/v1/, ''),
    );
    const params = Object.fromEntries(Object.entries(opts).filter(([, v]) => v != null));
    return (await axiosInstance.get<ScormLaunch>(url, { withCredentials: true, params })).data;
  },
  /** A free-preview package for someone signed in who isn't enrolled: browse mode, nothing saved. */
  getScormPreviewUrl: async (activityBlockId: number): Promise<ScormLaunch> =>
    (await axiosInstance.get<ScormLaunch>(`/v1/learning-objects/scorm/preview-url/${activityBlockId}`, { withCredentials: true })).data,
  /** The learner's attempts at a SCORM package and what opening it does next (Moodle's entry page). */
  getScormAttempts: async (activityBlockId: number): Promise<ScormAttemptSummary> =>
    (await axiosInstance.get<ScormAttemptSummary>(COURSE_CONTENT_ENDPOINTS.SCORM_ATTEMPTS(activityBlockId))).data,
  trackScormProgress: async (data: ApiScormTrackRequest): Promise<void> => {
    await axiosInstance.post(COURSE_CONTENT_ENDPOINTS.TRACK_SCORM, data);
  },
  launchLtiTool: async (activityBlockId: number): Promise<string> =>
    (await axiosInstance.get<string>(COURSE_CONTENT_ENDPOINTS.LAUNCH_LTI(activityBlockId), { responseType: 'text' })).data,
  receiveXapiStatement: async (xapiActivityId: number, statement: XapiStatementPayload): Promise<void> => {
    await axiosInstance.post(COURSE_CONTENT_ENDPOINTS.RECEIVE_XAPI_STATEMENT(xapiActivityId), statement);
  },
  getXapiStatements: async (xapiActivityId: number, params?: ApiGetXapiStatementsParams): Promise<ApiXapiStatement[]> =>
    (await axiosInstance.get<ApiXapiStatement[]>(COURSE_CONTENT_ENDPOINTS.GET_XAPI_STATEMENTS(xapiActivityId), { params })).data,
};
export interface ScormLaunch {
  playerUrl: string;
  attempt?: number;
  mode?: 'normal' | 'review';
}

export interface ScormAttemptRow {
  attempt: number;
  status: string;
  score: number | null;
  time_spent_seconds: number | null;
  started_at: string | null;
  last_activity_at: string | null;
  completed_at: string | null;
  finished: boolean;
}

export interface ScormAttemptSummary {
  attempts: ScormAttemptRow[];
  max_attempts: number;
  grading_method: 'highest' | 'average' | 'first' | 'last';
  attempts_used: number;
  can_start_new: boolean;
  next_action: 'start' | 'resume' | 'choose';
  grade: number | null;
}
